/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import MsDyn365, { IActionContext, ICoreContext, Image } from '@msdyn365-commerce/core';
import { ICartlinesViewProps, IOrderSummaryErrors } from '@msdyn365-commerce-modules/cart';
import { ICartLineItemViewProps } from '@msdyn365-commerce-modules/cart/dist/types/modules/cart/components/cart-line-items-promotion';
import { IInvoiceSummaryLines } from '@msdyn365-commerce-modules/invoice-payment-summary';
import { IOrderSummaryLines } from '@msdyn365-commerce-modules/order-summary-utilities';
import { ArrayExtensions } from '@msdyn365-commerce-modules/retail-actions';
import { getCartState } from '@msdyn365-commerce/global-state';
import {
    Button,
    getPayloadObject,
    getTelemetryAttributes,
    INodeProps,
    ITelemetryContent,
    Node,
    TelemetryConstant
} from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';

import { TelemetryEvent } from '@msdyn365-commerce/core';
import { CartLine, IDictionary, Warranty } from '@msdyn365-commerce/retail-proxy';
import { ICartState } from '@msdyn365-commerce/global-state';
import { pushGoogleAnalyticsEvent } from '../../../common/pushGoogleAnalyticsEvent';
import { CartGuide } from './components/CartGuide';
import { getApplicableWarrantiesAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/ProductsDataActions.g';
import { ICartViewProps } from '../../../modules/cart/cart';
import classnames from 'classnames';
import { getWarrantyLine, getRelatedCartLines, getLineIdFromExtProps } from '../../../modules/cart/components/cart-pick-up-in-store';
import { renderBonusItemLines } from '../../../modules/hei-buybox-v2/components/Popup';
import { ICartResources } from '../definition-extensions/cart.ext.props.autogenerated';

const _removeItemFromCartHandler = async (
    actionContext: IActionContext,
    cartlinesToRemove: string[],
    cartState: ICartState,
    cartLine: ICartlinesViewProps
) => {
    if (cartState) {
        const input = {
            cartLineIds: cartlinesToRemove
        };

        // Gather lines to be removed
        const lines = (cartState.cart.CartLines ?? []).filter(cl => cartlinesToRemove.indexOf(cl.LineId ?? '') >= 0);
        process.env.NODE_ENV === 'development' && console.log('GoogleAnalytics', '_removeItemFromCartHandler', cartLine, cartState.cart);

        await cartState.removeCartLines(input);

            const gaItems = lines.map((cl, i) => {
            const product = (cartLine.data?.cartline.LineId === cl.LineId && cartLine.data?.product) || null;
            const variant = product?.Dimensions?.map(d => d.DimensionValue?.Value).join(',');
            const brandFromExt = product?.ExtensionProperties?.find(p => p.Key === 'Brand')?.Value?.StringValue;
            const discount = (cl.DiscountLines ?? []).reduce((a, b) => a + (b.Amount ?? 0), 0);

            return {
                recId: product?.RecordId,
                item_id: product?.ProductNumber || product?.ItemId || cl.ItemId || '',
                item_name: product?.Name ?? '',
                index: i,
                item_variant: variant,
                price: (cl.Price ?? 0) - discount,
                discount: discount,
                pid: product?.ItemId || cl.ItemId || '',
                item_brand: brandFromExt ?? undefined,
                quantity: cl.Quantity ?? 0
            };
        });

        const totalValue = gaItems.reduce((a, b) => a + (b.price ?? 0) * b.quantity, 0);

        void pushGoogleAnalyticsEvent(
            {
                event: 'remove_from_cart',
                ecommerce: {
                    currency: 'CAD',
                    value: totalValue,
                    items: gaItems
                }
            },
            actionContext
        );
    }
};

const _renderCustomRemove = (
    actionContext: IActionContext,
    removeButtonText: string,
    attributes: IDictionary<string> | undefined,
    cartLinesToRemove: string[],
    cartState: ICartState,
    cartLine: ICartlinesViewProps
) => (
    <Button
        className='msc-cart-line__remove-item'
        onClick={() => _removeItemFromCartHandler(actionContext, cartLinesToRemove, cartState, cartLine)}
        title={removeButtonText}
        {...attributes}
    >
        {removeButtonText}
    </Button>
);

const _renderPromotions = (promotions: ICartLineItemViewProps): JSX.Element | undefined => {
    return (
        <>
            <Node {...promotions.promotionMessageWrapper}>
                {promotions.promotionMessage}
                {promotions.promotionSeeAllLink}
                {promotions.promotionDialog}
            </Node>
        </>
    );
};

const _getWarrantyLine = (cartLine: ICartlinesViewProps, cartState?: ICartState) => {
    return cartLine.data?.cartline.LineNumber ? getWarrantyLine(cartLine.data?.cartline.LineNumber, cartState) : null;
};

const _addWarrantyToCart = async (
    warranty: Warranty,
    productLine: ICartlinesViewProps,
    onActionProgress: (inProress: boolean) => void,
    coreContext: ICoreContext
) => {
    onActionProgress(true);
    const cartState = await getCartState(coreContext.actionContext);

    const warrantyLine = _getWarrantyLine(productLine, cartState);
    if (warrantyLine) {
        await cartState.removeCartLines({ cartLineIds: [warrantyLine.LineId!] });
    }

    const addResult = await cartState.addProductsToCart([
        {
            product: warranty,
            count: productLine.data?.cartline.Quantity
        }
    ]);
    if (addResult.status === 'SUCCESS') {
        const cartLines = cartState.cart.CartLines ?? [];
        const newWarrantyLine = cartLines[cartLines.length - 1];
        if (newWarrantyLine) {
            const productCartLine = cartLines.find(cl => cl.LineId === productLine.cartlineId);
            process.env.NODE_ENV === 'development' && console.log('_addWarrantyToCart', newWarrantyLine, productCartLine);
            newWarrantyLine.WarrantableLineNumber = productCartLine?.LineNumber;
            newWarrantyLine.IsWarrantyLine = true;

            const [productLineId, productLineUpdated] = getLineIdFromExtProps(productCartLine!);

            if (!newWarrantyLine.ExtensionProperties) {
                newWarrantyLine.ExtensionProperties = [];
            }

            newWarrantyLine.ExtensionProperties.push({
                Key: 'warrantable-line-id',
                Value: {
                    StringValue: productLineId
                }
            });

            newWarrantyLine.FulfillmentStoreId = productCartLine?.FulfillmentStoreId;
            newWarrantyLine.DeliveryMode = productCartLine?.DeliveryMode;
            newWarrantyLine.ShippingAddress = productCartLine?.ShippingAddress;

            const updatedCartLines = [newWarrantyLine];
            if (productLineUpdated) {
                updatedCartLines.push(productCartLine!);
            }

            await cartState.updateCartLines({ updatedCartLinesObject: updatedCartLines });
        }
    }

    onActionProgress(false);
};

const WarrantyOptionButton = (props: {
    warranty: Warranty;
    productLine: ICartlinesViewProps;
    warrantyLine: CartLine | null;
    coreContext: ICoreContext;
    onActionProgress: (inProress: boolean) => void;
}) => {
    const { warranty, productLine, warrantyLine, onActionProgress, coreContext } = props;
    const unit = warranty.DurationUnitValue ?? 0;
    const duration = warranty.Duration ?? 0;
    const label =
        unit === 1
            ? duration > 1
                ? 'days'
                : 'day'
            : unit === 2
            ? duration > 1
                ? 'months'
                : 'month'
            : unit === 3
            ? duration > 1
                ? 'years'
                : 'year'
            : '';

    return (
        <button
            className={classnames('msc-btn', {
                active: warranty.RecordId === warrantyLine?.ProductId
            })}
            type='button'
            disabled={warranty.RecordId === warrantyLine?.ProductId}
            onClick={() => _addWarrantyToCart(warranty, productLine, onActionProgress, coreContext)}
        >
            {`${duration} ${label}`}
        </button>
    );
};

type ProductWarrantySectionProps = {
    warranties: Warranty[];
    coreContext: ICoreContext;
    cartLine: ICartlinesViewProps;
    cartState: ICartState;
    setActionInProgress: (inProgress: boolean) => void;
};

const ProductWarrantySection = ({ warranties, coreContext, cartLine, cartState, setActionInProgress }: ProductWarrantySectionProps) => {
    const [isExpanded, setIsExpanded] = React.useState(false);
    const selectedWarranty = _getWarrantyLine(cartLine, cartState);

    process.env.NODE_ENV === 'development' && console.log('ProductWarrantySection', selectedWarranty);
    return (
        <div className='msc-cart-line__warranty-section'>
            <div className='msc-cart-line__product-image'>
                <Image
                    requestContext={coreContext.request}
                    gridSettings={coreContext.request.gridSettings!}
                    src={coreContext.request.apiSettings.baseImageUrl + 'Products/479HEN109_000_001.png'}
                    imageSettings={{
                        viewports: {
                            xs: { q: 'w=183&m=8', w: 183, h: 0 },
                            lg: { q: 'w=183&m=8', w: 183, h: 0 }
                        },
                        lazyload: true,
                        cropFocalRegion: true
                    }}
                    altText='HELP'
                    bypassHideOnFailure
                />
            </div>
            <Node className='msc-cart-line__extra-actions'>
                <div>
                    <p>
                        Breathe easy when you protect your gear with Henry's Extended Life Plan, an extended warranty that offers far more
                        than the manufacturer's warranty.
                    </p>
                    <p>
                        <a
                            href='#'
                            onClick={evt => {
                                evt.preventDefault();
                                setIsExpanded(!isExpanded);
                            }}
                        >
                            View Benefits {/* TODO: replace with icon */}
                            {isExpanded ? <span className='view-benefits-close'></span> : <span className='view-benefits-open'></span>}
                        </a>
                    </p>
                    {isExpanded && (
                        <>
                            <p>
                                H.E.L.P. adds benefits from the date of purchase, and <strong>adds years of coverage</strong> to the
                                manufacturer's warranty, instead of replacing it like other retailers.
                            </p>
                            <ul className='check'>
                                <li>100% Parts &amp; Labour Covered*</li>
                                <li>50% Off At-Fault Repairs</li>
                                <li>Global Coverage and more!</li>
                            </ul>
                            <p>
                                Wherever life takes you and your equipment, Henry's has you covered. Plus, H.E.L.P. is fully administered by
                                Henry's, so you never have to consult a third-party company for approval or repairs. Simply contact your
                                local Henry's for assistance.
                            </p>
                            <p>
                                <a href='/henrys-extended-life-plan'>View All Benefits</a>
                            </p>
                            <p className='small'>
                                *See <a href='/henrys-extended-life-plan#terms'>Terms &amp; Conditions</a> for full details and restrictions.
                            </p>
                        </>
                    )}
                    <p className='heading'>Choose your coverage:</p>
                    <div className='warranty-chooser'>
                        {warranties.map((w, idx) => (
                            <WarrantyOptionButton
                                key={w.ItemId ?? idx}
                                warranty={w}
                                productLine={cartLine}
                                warrantyLine={selectedWarranty}
                                coreContext={coreContext}
                                onActionProgress={inProgress => setActionInProgress(inProgress)}
                            />
                        ))}
                        <button
                            className={classnames('msc-btn', {
                                active: !selectedWarranty
                            })}
                            type='button'
                            disabled={!selectedWarranty}
                            onClick={() =>
                                selectedWarranty &&
                                _removeItemFromCartHandler(coreContext.actionContext, [selectedWarranty.LineId!], cartState, cartLine)
                            }
                        >
                            {`No coverage`}
                        </button>
                        <span className='warranty-price'>
                            {`${coreContext.cultureFormatter.formatCurrency(
                                selectedWarranty?.TotalAmount ?? selectedWarranty?.Price ?? 0
                            )}`}
                            {(selectedWarranty?.DiscountAmount ?? 0) > 0 && (
                                <span className='msc-price__savings'>
                                    {`Save ${coreContext.cultureFormatter.formatCurrency(selectedWarranty?.DiscountAmount ?? 0)}`}
                                </span>
                            )}
                        </span>
                    </div>
                </div>
            </Node>
        </div>
    );
};

const CartLines = (props: {
    cartLines: ICartlinesViewProps[] | undefined;
    resources: ICartResources;
    storeSelector: React.ReactNode | undefined;
    backToShoppingButton: React.ReactNode;
    waitingComponent: React.ReactNode;
    cartLoadingStatus: string;
    cartDataResult: boolean;
    cartState: ICartState | undefined;
    telemetryContent: ITelemetryContent;
    coreContext: ICoreContext;
    mulitplePickUp?: boolean;
    allCartLines: ICartlinesViewProps[];
}) => {
    const {
        cartLines,
        resources,
        storeSelector,
        backToShoppingButton,
        waitingComponent,
        cartLoadingStatus,
        cartDataResult,
        cartState,
        telemetryContent,
        coreContext,
        allCartLines,
        mulitplePickUp
    } = props;

    const actionContext = coreContext.actionContext;
    const emailDeliveryModeCode = actionContext.requestContext.channel?.EmailDeliveryModeCode;

    const { emptyCartText, removeCartButtonText: removeButtonText } = resources;
    const payload = getPayloadObject(TelemetryEvent.RemoveFromCart, telemetryContent, removeButtonText, '');
    const attributes = getTelemetryAttributes(telemetryContent, payload);

    const [actionInProgress, setActionInProgress] = React.useState(false);

    const [warrantyGroups, setWarrantyGroups] = React.useState<Record<number, Warranty[]> | null>(null);
    const catalogId = 0;
    React.useEffect(() => {
        const allProductRecIds = ArrayExtensions.unique(
            cartLines
                ?.filter(cl => !cl.data?.cartline.IsWarrantyLine)
                .map(cl => cl.data?.product?.RecordId ?? 0)
                .filter(recId => recId > 0) ?? []
        );

        setWarrantyGroups(null);
        if (allProductRecIds.length > 0) {
            (async () => {
                const data = await getApplicableWarrantiesAsync({ callerContext: actionContext }, catalogId, allProductRecIds);

                const map: Record<number, Warranty[]> = {};
                for (const m of data) {
                    if (m.WarrantyGroups && (m.WarrantyGroups[0]?.Warranties?.length ?? 0) > 0) {
                        map[m.WarrantableProductId!] = (m.WarrantyGroups[0].Warranties ?? []).sort(
                            (a, b) => (a.Duration ?? 0) - (b.Duration ?? 0)
                        );
                    }
                }

                setWarrantyGroups(map);
                process.env.NODE_ENV === 'development' && console.log('CartLine', 'getApplicableWarrantiesAsync', allProductRecIds, map);
            })();
        } else {
            setWarrantyGroups({});
        }
    }, [catalogId, actionContext, cartLines]);

    const getCartLinesToRemove = (cartLine: ICartlinesViewProps) => {
        const relatedLines = getRelatedCartLines(cartLine.data?.cartline!, cartState).map(cl => cl.LineId!);
        if (relatedLines.length > 0) {
            return [cartLine.cartlineId!, ...relatedLines];
        }

        return [cartLine.cartlineId!];
    };

    if (cartLoadingStatus) {
        return <>{cartLoadingStatus}</>;
    }

    if (!actionInProgress && warrantyGroups && cartLines) {
        return (
            <>
                {cartLines.map((cartLine, index) => {
                    const isWarranty = cartLine.data?.cartline.IsWarrantyLine ?? false;

                    if (isWarranty) {
                        process.env.NODE_ENV === 'development' && console.log('CartLines', 'skip warranty line');

                        if (cartLine.data?.cartline.WarrantableLineNumber) {
                            return null;
                        }
                    } else {
                        const isBonus =
                            cartLine.data?.cartline.TotalAmount === 0 &&
                            Boolean(cartLine.data?.cartline.ExtensionProperties?.find(e => e.Key === 'bonus-for-line-id'));
                        if (isBonus) {
                            process.env.NODE_ENV === 'development' && console.log('CartLines', 'skip bonus line', cartLine);
                            return null;
                        }
                    }

                    let product = cartLine.data?.product;
                    const warranties = warrantyGroups[product?.RecordId ?? 0];

                    const bonusProducts = getRelatedCartLines(cartLine.data?.cartline!, cartState)
                        .filter(cl => !cl.IsWarrantyLine)
                        // search bonus line from the entire cart
                        .map(cl => allCartLines.find(c => c.cartlineId === cl.LineId))
                        .filter(cl => !!cl?.data?.product)
                        .map(cl => ({
                            ...cl!.data!.product!,
                            shipped: !!cartLine.data?.cartline!.DeliveryMode && !cl!.data!.cartline.DeliveryMode
                        }));

                    process.env.NODE_ENV === 'development' &&
                        console.log('_renderCartlines', cartLine.cartlineId, cartLine.data?.cartline!.DeliveryMode, bonusProducts);

                    return (
                        <div
                            className={classnames('msc-cart-lines-item', {
                                'is-warranty-line': isWarranty
                            })}
                            key={`${index}-${MsDyn365.isBrowser.toString()}`}
                        >
                            {cartLine.cartline}
                            {bonusProducts && bonusProducts.length > 0 && (
                                <Node className='msc-cart-line-group__extra-actions'>
                                    {renderBonusItemLines(
                                        bonusProducts,
                                        coreContext,
                                        'msc-cart-lines-item',
                                        resources.bonusItemShippingLabel
                                    )}
                                </Node>
                            )}
                            {mulitplePickUp
                                ? null
                                : _renderBOPISBlock(cartLine, resources, storeSelector, telemetryContent, emailDeliveryModeCode)}
                            {mulitplePickUp ? (
                                <Node className='msc-cart-line-group__extra-actions'>
                                    {_renderGroupBOPISBlock(cartLine, resources, storeSelector, telemetryContent, emailDeliveryModeCode)}
                                    {cartLine.addToOrderTemplate}
                                    {cartLine.addToWishlist}
                                    {cartState &&
                                        _renderCustomRemove(
                                            actionContext,
                                            removeButtonText,
                                            attributes,
                                            getCartLinesToRemove(cartLine),
                                            cartState,
                                            cartLine
                                        )}
                                </Node>
                            ) : cartLine.addToOrderTemplate ? (
                                <Node className='msc-cart-line__extra-actions'>
                                    {cartLine.addToOrderTemplate}
                                    {cartLine.addToWishlist}
                                    {cartState &&
                                        _renderCustomRemove(
                                            actionContext,
                                            removeButtonText,
                                            attributes,
                                            getCartLinesToRemove(cartLine),
                                            cartState,
                                            cartLine
                                        )}
                                </Node>
                            ) : (
                                <>
                                    {cartLine.addToWishlist}
                                    {cartState &&
                                        _renderCustomRemove(
                                            actionContext,
                                            removeButtonText,
                                            attributes,
                                            getCartLinesToRemove(cartLine),
                                            cartState,
                                            cartLine
                                        )}
                                </>
                            )}
                            {warranties && (
                                <ProductWarrantySection
                                    warranties={warranties}
                                    setActionInProgress={setActionInProgress}
                                    cartLine={cartLine}
                                    cartState={cartState!}
                                    coreContext={coreContext}
                                />
                            )}
                        </div>
                    );
                })}
            </>
        );
    }
    return cartDataResult ? (
        <div className='msc-cart__empty-cart'>
            <p className='msc-cart-line'>{emptyCartText}</p>
            {backToShoppingButton}
        </div>
    ) : (
        <>{waitingComponent}</>
    );
};

/**
 * On Toggle function.
 * @param cartLine -Cartline.
 * @param isBopisSelected -Boolean.
 * @returns Set state of button.
 */
const onToggleBopisHandler = (cartLine: ICartlinesViewProps, isBopisSelected: boolean) => () => {
    cartLine.pickUpInStore && cartLine.pickUpInStore.callbacks.toggleBopis(!isBopisSelected);
};

/**
 * On change store function.
 * @param cartLine -CartLine items prop.
 * @returns Set state of button.
 */
const onChangeStoreHandler = (cartLine: ICartlinesViewProps) => () => {
    cartLine.pickUpInStore && cartLine.pickUpInStore.callbacks.toggleBopis(true);
};

const _renderBOPISBlock = (
    cartLine: ICartlinesViewProps,
    resources: ICartResources,
    storeSelector: React.ReactNode | undefined,
    telemetryContent?: ITelemetryContent,
    emailDeliveryModeCode?: string
): JSX.Element | null => {
    // If it is electronic item cart line, then return null for BOPISBlock.
    if (!cartLine.pickUpInStore || !storeSelector || cartLine.data?.cartline.DeliveryMode === emailDeliveryModeCode) {
        return null;
    }

    const {
        shipInsteadDisplayText,
        shipToAddressDisplayText,
        pickItUpDisplayText,
        pickUpAtStoreWithLocationText,
        changeStoreDisplayText
    } = resources;

    const isWarranty = cartLine.data?.cartline.IsWarrantyLine ?? false;
    const isBopisSelected = cartLine.pickUpInStore.isBopisSelected;

    const payLoad = getPayloadObject('click', telemetryContent!, TelemetryConstant.PickupInStore);
    const puckUpinStoreAttribute = getTelemetryAttributes(telemetryContent!, payLoad);

    const isBopisOnly = Boolean(
        cartLine.data?.product?.ExtensionProperties?.find(ep => ep.Key === 'Bopis' && ep.Value?.StringValue === 'Yes')
    );
    process.env.NODE_ENV === 'development' && console.log('_renderBOPISBlock', isBopisOnly, cartLine.data?.product);

    return (
        <Node {...cartLine.pickUpInStore.ContainerProps}>
            <div className='msc-cart-line__bopis-method'>
                {isBopisSelected ? (
                    <span className='pick-up'>{pickUpAtStoreWithLocationText}</span>
                ) : (
                    <span className='ship'>{shipToAddressDisplayText}</span>
                )}
            </div>
            {isBopisSelected && !isWarranty && (
                <div className='msc-cart-line__bopis-fullfilment'>
                    <span className='msc-cart-line__bopis-fullfilment-store'>{cartLine.pickUpInStore.orgUnitName}</span>
                    <Button role='link' className='msc-cart-line__bopis-changestore' onClick={onChangeStoreHandler(cartLine)}>
                        {changeStoreDisplayText}
                    </Button>
                </div>
            )}
            {!isWarranty && !isBopisOnly && (
                <Button
                    className='msc-cart-line__bopis-btn'
                    {...puckUpinStoreAttribute}
                    onClick={onToggleBopisHandler(cartLine, isBopisSelected)}
                >
                    {isBopisSelected ? shipInsteadDisplayText : pickItUpDisplayText}
                </Button>
            )}
        </Node>
    );
};

const _renderErrorBlock = (errorData: IOrderSummaryErrors | undefined): JSX.Element | null => {
    if (!errorData || errorData.errors.length === 0) {
        return null;
    }
    return (
        <Node {...errorData.Wrapper}>
            {errorData.header}
            {errorData.errors}
        </Node>
    );
};

const _renderOrderSummarylines = (
    orderSummaryLines: IOrderSummaryLines | undefined,
    OrderSummaryItems: INodeProps,
    props: ICartViewProps
): JSX.Element | null => {
    if (!orderSummaryLines) {
        return null;
    }
    return (
        <Node {...OrderSummaryItems}>
            {props.promoCode}
            {orderSummaryLines.subtotal}
            {orderSummaryLines.shipping}
            {orderSummaryLines.otherCharge}
            {orderSummaryLines.tax}
            {orderSummaryLines.totalDiscounts ? orderSummaryLines.totalDiscounts : null}
            {orderSummaryLines.orderTotal}
        </Node>
    );
};

const _renderInvoiceSummarylines = (
    invoiceSummaryLines: IInvoiceSummaryLines | undefined,
    OrderSummaryItems: INodeProps,
    props: ICartViewProps
): JSX.Element | null => {
    if (!invoiceSummaryLines) {
        props.context.telemetry.error('InvoiceSummary content is empty, module wont render');
        return null;
    }
    return (
        <Node {...OrderSummaryItems}>
            {invoiceSummaryLines.invoices}
            {invoiceSummaryLines.giftCard}
            {invoiceSummaryLines.loyalty}
            {invoiceSummaryLines.orderTotal}
        </Node>
    );
};

const _renderCartLinesGroup = (props: ICartViewProps, resources: ICartResources): JSX.Element | undefined => {
    if (props.cartLinesGroup && props.cartLinesGroup.length > 0) {
        const allCartLines = props.cartLinesGroup.flatMap(cartlines => cartlines);
        return (
            <div className='msc-cart-lines-group'>
                {props.cartLinesGroup.map(cartlines => {
                    const itemCount = _countItems(cartlines);
                    if (itemCount === 0) {
                        return null;
                    }

                    return (
                        <div key={cartlines[0].cartlineId} className='msc-cart-lines-group-wraper'>
                            {_renderCartLinesGroupHeader(
                                cartlines[0],
                                props.storeSelector,
                                resources,
                                itemCount,
                                props.context.actionContext.requestContext.channel?.EmailDeliveryModeCode
                            )}
                            <CartLines
                                cartLines={cartlines}
                                resources={props.resources}
                                storeSelector={props.storeSelector}
                                backToShoppingButton={props.backToShoppingButton}
                                waitingComponent={props.waitingComponent}
                                cartLoadingStatus={props.cartLoadingStatus}
                                cartDataResult={props.cartDataResult}
                                cartState={props.data.cart.result}
                                telemetryContent={props.telemetryContent!}
                                coreContext={props.context}
                                mulitplePickUp={props.multiplePickUpEnabled}
                                allCartLines={allCartLines}
                            />
                        </div>
                    );
                })}

                {props.slots?.afterCartlines?.map(n => (
                    <div className='msc-cart-lines-footer-module'>{n}</div>
                ))}
            </div>
        );
    }
    return props.cartDataResult ? (
        <div className='msc-cartline-wraper'>
            <div className='msc-cart__empty-cart'>
                <p className='msc-cart-line'>{props.resources.emptyCartText}</p>
                {props.backToShoppingButton}
            </div>

            {props.slots?.afterCartlines?.map(n => (
                <div className='msc-cart-lines-footer-module'>{n}</div>
            ))}
        </div>
    ) : (
        <>{props.waitingComponent}</>
    );
};
const _renderCartLinesGroupHeader = (
    cartLine: ICartlinesViewProps,
    storeSelector: React.ReactNode | undefined,
    resources: ICartResources,
    count: number,
    emailDeliveryModeCode?: string
) => {
    const isBopisSelected = cartLine.pickUpInStore?.isBopisSelected;
    const { pickUpText, shippingText, emailshippingText } = resources;
    let groupTitle: string;

    groupTitle = isBopisSelected ? pickUpText : shippingText;
    groupTitle = cartLine.data && cartLine.data.cartline.DeliveryMode === emailDeliveryModeCode ? emailshippingText : groupTitle;

    return (
        <>
            <div className='msc-cart-lines-group-wraper__bopis-heading'>
                <p className={`msc-cart-lines-group-wraper__bopis-heading-${groupTitle.toLowerCase()}-icon`} />
                <p className={`msc-cart-lines-group-wraper__bopis-heading-${groupTitle.toLowerCase()}`}>{groupTitle}</p>
                {_renderCartLinesGroupTitle(cartLine, resources, count)}
            </div>
        </>
    );
};

const _renderGroupBOPISBlock = (
    cartLine: ICartlinesViewProps,
    resources: ICartResources,
    storeSelector: React.ReactNode | undefined,
    telemetryContent?: ITelemetryContent,
    emailDeliveryModeCode?: string
): JSX.Element | null => {
    // If it is electronic item cart line, then return null for GroupBOPISBlock.
    if (!cartLine.pickUpInStore || !storeSelector || cartLine.data?.cartline.DeliveryMode === emailDeliveryModeCode) {
        return null;
    }

    const { changeStoreDisplayText, shipInsteadDisplayText, pickItUpDisplayText } = resources;

    const isWarranty = cartLine.data?.cartline.IsWarrantyLine ?? false;
    const isBopisSelected = cartLine.pickUpInStore.isBopisSelected;

    const payLoad = getPayloadObject('click', telemetryContent!, TelemetryConstant.PickupInStore);
    const puckUpinStoreAttribute = getTelemetryAttributes(telemetryContent!, payLoad);

    const isBopis = Boolean(cartLine.data?.product?.ExtensionProperties?.find(ep => ep.Key === 'Bopis' && ep.Value?.StringValue === 'Yes'));
    process.env.NODE_ENV === 'development' && console.log('_renderBOPISBlock', isBopis, cartLine.data?.product);

    return (
        <>
            {isBopisSelected && !isWarranty ? (
                <Button className='msc-cart-line__remove-item msc-btn' onClick={onChangeStoreHandler(cartLine)} aria-live='polite'>
                    {changeStoreDisplayText}
                </Button>
            ) : null}
            {!isWarranty && !isBopis && (
                <Button
                    className='msc-cart-line__remove-item msc-btn'
                    {...puckUpinStoreAttribute}
                    onClick={onToggleBopisHandler(cartLine, isBopisSelected)}
                >
                    {isBopisSelected ? shipInsteadDisplayText : pickItUpDisplayText}
                </Button>
            )}
        </>
    );
};

const _countItems = (cartLine: ICartlinesViewProps[]): number => {
    let countItem = 0;
    countItem = cartLine
        .filter(
            cl => !cl.data?.cartline.IsWarrantyLine && !cl.data?.cartline.ExtensionProperties?.find(ex => ex.Key === 'bonus-for-line-id')
        )
        .reduce((count, item) => {
            return count + (item.data?.cartline.Quantity || 0);
        }, 0);
    return countItem;
};

const _renderCartLinesGroupTitle = (cartLine: ICartlinesViewProps, resources: ICartResources, count: number) => {
    const isBopisSelected = cartLine.pickUpInStore?.isBopisSelected;
    const { itemLabel, itemsLabel } = resources;
    const suffix = count > 1 ? itemsLabel : itemLabel;
    return (
        <>
            <Node className='msc-cart-lines-group-wraper__bopis-heading-title'>
                {isBopisSelected ? (
                    <p className='msc-cart-lines-group-wraper__bopis-heading-title-st'>
                        {cartLine.pickUpInStore?.deliveryOption}
                        {', '}
                        {cartLine.pickUpInStore?.orgUnitName}
                    </p>
                ) : null}
                <p className='msc-cart-lines-group-wraper__bopis-heading-title-ct'>
                    ({count}
                    {` `}
                    {suffix})
                </p>
            </Node>
        </>
    );
};

const CartView: React.FC<ICartViewProps> = (props: ICartViewProps) => (
    <div className={props.className} id={props.id} {...props.renderModuleAttributes(props)}>
        {props.title}
        {props.promotionOptions && _renderPromotions(props.promotionOptions)}
        {props.multiplePickUpEnabled ? (
            _renderCartLinesGroup(props, props.resources)
        ) : (
            <div style={{ display: 'flex', flexDirection: 'column' }}>
                <Node {...props.CartlinesWrapper}>
                    <CartLines
                        cartLines={props.cartlines}
                        resources={props.resources}
                        storeSelector={props.storeSelector}
                        backToShoppingButton={props.backToShoppingButton}
                        waitingComponent={props.waitingComponent}
                        cartLoadingStatus={props.cartLoadingStatus}
                        cartDataResult={props.cartDataResult}
                        cartState={props.data.cart.result}
                        telemetryContent={props.telemetryContent!}
                        coreContext={props.context}
                        mulitplePickUp={props.multiplePickUpEnabled}
                        allCartLines={props.cartlines ?? []}
                    />
                </Node>
            </div>
        )}
        {props.orderSummaryHeading && (
    <>
        <Node {...props.OrderSummaryWrapper}>
            {props.orderSummaryHeading}
            {props.cart?.hasInvoiceLine
                ? _renderInvoiceSummarylines(props.invoiceSummaryLineitems, props.OrderSummaryItems, props)
                : _renderOrderSummarylines(props.orderSummaryLineitems, props.OrderSummaryItems, props)}
            {_renderErrorBlock(props.OrderSummaryErrors)}
            {props.checkoutAsSignInUserButton}
            {props.checkoutAsGuestButton}
            {props.expressCheckoutButton && ArrayExtensions.hasElements(props.expressCheckoutButton) ? (
        <Node {...props.ExpressCheckoutSectionWrapper}>{props.expressCheckoutButton}</Node>
            ) : null}
            {props.backToShoppingButton}
            {props.createTemplateFromCartButton}
        </Node>
        <CartGuide />
    </>
)}
        {props.storeSelector}
    </div>
);

export default CartView;